<template>  <div class="app-notification-panel">
    <div class="app-notification-panel__header">
      <div class="app-notification-panel__header-content">
        <div class="app-notification-panel__title-section">
          <h3 class="app-notification-panel__title">{{ $t('notifications.title') }}</h3>
          <div v-if="unreadCount > 0" class="app-notification-panel__count-badge">
            {{ unreadCount }}
          </div>
        </div>
        <div class="app-notification-panel__header-actions">
          <button 
            @click="$emit('refresh')" 
            class="app-notification-panel__action-btn app-notification-panel__action-btn--refresh"
            :title="$t('notifications.refresh')"
            :disabled="loading"
          >
            <svg 
              class="app-notification-panel__refresh-icon" 
              :class="{ 'app-notification-panel__refresh-icon--rotating': loading }"
              viewBox="0 0 24 24" 
              width="18" 
              height="18"
            >
              <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
            </svg>
          </button>
          <button 
            @click="$emit('close')" 
            class="app-notification-panel__action-btn app-notification-panel__action-btn--close"
            :title="$t('common.close')"
          >
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="app-notification-panel__content">      <!-- Loading State -->
      <div v-if="loading" class="app-notification-panel__loading">
        <div class="app-notification-panel__loading-content">
          <div class="app-notification-panel__loading-spinner">
            <div class="app-notification-panel__spinner-ring"></div>
            <div class="app-notification-panel__spinner-ring"></div>
            <div class="app-notification-panel__spinner-ring"></div>
          </div>
          <p class="app-notification-panel__loading-text">{{ $t('notifications.loading') }}</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="app-notification-panel__error">
        <div class="app-notification-panel__error-content">
          <div class="app-notification-panel__error-icon">
            <svg viewBox="0 0 24 24" width="56" height="56">
              <path fill="currentColor" d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z" />
            </svg>
          </div>
          <h4 class="app-notification-panel__error-title">{{ $t('notifications.errorTitle') }}</h4>
          <p class="app-notification-panel__error-message">{{ error }}</p>
          <button @click="$emit('refresh')" class="app-button app-button--secondary app-button--small">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
            </svg>
            {{ $t('notifications.retry') }}
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="notifications.length === 0" class="app-notification-panel__empty">
        <div class="app-notification-panel__empty-content">
          <div class="app-notification-panel__empty-icon">
            <svg viewBox="0 0 24 24" width="72" height="72">
              <path fill="currentColor" d="M12,2C13.1,2 14,2.9 14,4C14,4.1 14,4.2 13.9,4.3C16.3,5.2 18,7.5 18,10.1V15.5C18,16.8 18.6,18 19.6,18.7L20,19V20H4V19L4.4,18.7C5.4,18 6,16.8 6,15.5V10.1C6,7.5 7.7,5.2 10.1,4.3C10,4.2 10,4.1 10,4C10,2.9 10.9,2 12,2M10,21C10,22.1 10.9,23 12,23C13.1,23 14,22.1 14,21" />
            </svg>
            <div class="app-notification-panel__empty-decoration"></div>
          </div>
          <h4 class="app-notification-panel__empty-title">{{ $t('notifications.noNotifications') }}</h4>
          <p class="app-notification-panel__empty-description">{{ $t('notifications.noNotificationsDescription') }}</p>
        </div>
      </div>      <!-- Notifications List -->
      <div v-else class="app-notification-panel__list">
        <div 
          v-for="(notif, index) in notifications" 
          :key="notif.id"
          class="app-notification-panel__item"
          :class="{ 'app-notification-panel__item--unread': !notif.read }"
          :style="{ animationDelay: `${index * 50}ms` }"
        >
          <div class="app-notification-panel__item-content" @click="handleNotificationClick(notif)">
            <div class="app-notification-panel__icon">
              <div class="app-notification-panel__icon-bg"
                   :class="`app-notification-panel__icon-bg--${getNotificationVariant(notif)}`">
                <i :class="`fas ${getNotificationIcon(notif)}`" class="app-notification-panel__icon-svg"></i>
              </div>
              <div v-if="!notif.read" class="app-notification-panel__unread-indicator"></div>
            </div>
            
            <div class="app-notification-panel__content-wrapper">
              <div class="app-notification-panel__header-row">
                <h5 class="app-notification-panel__item-title">{{ getNotificationTitle(notif) }}</h5>
                <span class="app-notification-panel__item-time">{{ formatNotificationTime(notif.createdAt) }}</span>
              </div>
              <p class="app-notification-panel__item-message">{{ getNotificationMessage(notif) }}</p>
            </div>
          </div>
          
          <div class="app-notification-panel__actions">
            <!-- Mark as read only (small icon button) -->
            <button 
              v-if="!notif.read"
              @click="handleMarkAsReadOnly(notif, $event)"
              class="app-notification-panel__action-btn app-notification-panel__action-btn--check"
              :title="$t('notifications.markAsRead')"
            >
              <i class="fas fa-check"></i>
            </button>
            
            <!-- Delete notification -->
            <button 
              @click="handleDeleteNotification(notif, $event)"
              class="app-notification-panel__action-btn app-notification-panel__action-btn--delete"
              :title="$t('notifications.delete')"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import type { UserNotificationDTO } from '@/types'
import { useUserNotifications } from '@/composables/useUserNotifications'
import userNotificationService from '@/services/userNotification.service'
import { showApiSuccessNotification, showApiErrorNotification } from '@/utils/globalNotifications'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  notifications: UserNotificationDTO[]
  loading?: boolean
  error?: string | null
}>()

const emit = defineEmits<{
  close: []
  refresh: []
}>()

const router = useRouter()
const { t } = useI18n()
const {
  getNotificationTitle,
  getNotificationMessage,
  getNotificationActionUrl,
  formatNotificationTime,
  getNotificationIcon,
  getNotificationVariant
} = useUserNotifications()

const unreadCount = computed(() => {
  return props.notifications.filter((n: UserNotificationDTO) => !n.read).length
})

/**
 * Navigate to the notification's target page and mark as read
 */
const handleNotificationClick = async (notif: UserNotificationDTO) => {
  try {
    // Mark as read
    if (!notif.read) {
      await userNotificationService.markAsRead(notif.id)
    }
    
    // Navigate to action URL
    const url = getNotificationActionUrl(notif)
    if (url) {
      await router.push(url)
      emit('close')
    }
    
    // Refresh the notification list
    emit('refresh')
  } catch (error) {
    console.error('Error handling notification click:', error)
    showApiErrorNotification({
      type: 'ERROR',
      message: t('notifications.errors.navigation'),
      code: 'NAVIGATION_ERROR',
      timestamp: new Date().toISOString()
    })
  }
}

/**
 * Mark notification as read without navigating
 */
const handleMarkAsReadOnly = async (notif: UserNotificationDTO, event: Event) => {
  event.stopPropagation() // Prevent navigation
  
  try {
    await userNotificationService.markAsRead(notif.id)
    showApiSuccessNotification(t('notifications.markedAsRead'))
    emit('refresh')
  } catch (error) {
    console.error('Error marking notification as read:', error)
    showApiErrorNotification({
      type: 'ERROR',
      message: t('notifications.errors.markAsRead'),
      code: 'MARK_READ_ERROR',
      timestamp: new Date().toISOString()
    })
  }
}

/**
 * Delete notification
 */
const handleDeleteNotification = async (notif: UserNotificationDTO, event: Event) => {
  event.stopPropagation() // Prevent navigation
  
  try {
    await userNotificationService.deleteNotification(notif.id)
    showApiSuccessNotification(t('notifications.deleted'))
    emit('refresh')
  } catch (error) {
    console.error('Error deleting notification:', error)
    showApiErrorNotification({
      type: 'ERROR',
      message: t('notifications.errors.delete'),
      code: 'DELETE_ERROR',
      timestamp: new Date().toISOString()
    })
  }
}
</script>

<style scoped>
.app-notification-panel {
  background: var(--bg-primary, #ffffff);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  max-height: 500px;
  min-height: 320px;
  width: 100%;
  display: flex;
  flex-direction: column;
  animation: panel-entrance var(--transition-duration-slow) cubic-bezier(0.4, 0, 0.2, 1);
  /* Ensure background is never transparent */
  backdrop-filter: none;
}

@keyframes panel-entrance {
  0% {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.app-notification-panel__header {
  background: var(--bg-secondary, #f8f9fa);
  border-bottom: 1px solid var(--border-color);
  /* Ensure header background is solid */
  backdrop-filter: none;
}

.app-notification-panel__header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--space-md) var(--space-lg);
}

.app-notification-panel__title-section {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.app-notification-panel__title {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.app-notification-panel__count-badge {
  background: var(--error);
  color: var(--white);
  border-radius: var(--border-radius-full);
  min-width: 24px;
  height: 24px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--space-sm);
  animation: badge-pulse 2s ease-in-out infinite;
}

@keyframes badge-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.app-notification-panel__header-actions {
  display: flex;
  gap: var(--space-sm);
}

.app-notification-panel__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-duration) cubic-bezier(0.4, 0, 0.2, 1);
}

.app-notification-panel__action-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border-color: var(--primary);
}

.app-notification-panel__action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.app-notification-panel__refresh-icon--rotating {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.app-notification-panel__content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Loading state */
.app-notification-panel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: var(--space-lg);
  min-height: 200px;
}

.app-notification-panel__loading-content {
  text-align: center;
}

.app-notification-panel__loading-spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto var(--space-lg);
}

.app-notification-panel__spinner-ring {
  position: absolute;
  width: 60px;
  height: 60px;
  border: 4px solid transparent;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.app-notification-panel__spinner-ring:nth-child(2) {
  animation-delay: -0.5s;
  border-top-color: var(--primary-light);
}

.app-notification-panel__spinner-ring:nth-child(3) {
  animation-delay: -1s;
  border-top-color: var(--primary-lighter);
}

.app-notification-panel__loading-text {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
}

/* Error state */
.app-notification-panel__error {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: var(--space-lg);
  min-height: 200px;
}

.app-notification-panel__error-content {
  text-align: center;
  max-width: 300px;
}

.app-notification-panel__error-icon {
  color: var(--error);
  margin-bottom: var(--space-lg);
}

.app-notification-panel__error-title {
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0 0 var(--space-sm);
}

.app-notification-panel__error-message {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0 0 var(--space-lg);
  line-height: var(--line-height-relaxed);
}

/* Empty state */
.app-notification-panel__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  padding: var(--space-lg);
  min-height: 200px;
}

.app-notification-panel__empty-content {
  text-align: center;
  max-width: 280px;
}

.app-notification-panel__empty-icon {
  position: relative;
  color: var(--text-disabled);
  margin-bottom: var(--space-lg);
}

.app-notification-panel__empty-decoration {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: var(--warning);
  border-radius: 50%;
  opacity: 0.3;
}

.app-notification-panel__empty-title {
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0 0 var(--space-sm);
}

.app-notification-panel__empty-description {
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
  line-height: var(--line-height-relaxed);
}

/* Notification list */
.app-notification-panel__list {
  padding: var(--space-sm);
  overflow-y: auto;
  max-height: 350px;
  flex: 1;
}

.app-notification-panel__item {
  background: var(--bg-primary, #ffffff);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--space-md);
  transition: all var(--transition-duration) ease;
  animation: notification-slide-in 0.3s ease forwards;
  opacity: 0;
  transform: translateX(20px);
  overflow: hidden;
  position: relative;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.app-notification-panel__item--unread {
  border-left: 4px solid var(--primary);
  border-color: var(--primary-light);
  background: linear-gradient(to right, rgba(25, 118, 210, 0.04), var(--bg-primary, #ffffff));
}

.app-notification-panel__item:hover {
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

@keyframes notification-slide-in {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.app-notification-panel__item-content {
  display: flex;
  align-items: flex-start;
  padding: var(--space-md);
  gap: var(--space-md);
  cursor: pointer;
  flex: 1;
}

.app-notification-panel__item-content:hover .app-notification-panel__item-title {
  color: var(--primary);
}

.app-notification-panel__icon {
  position: relative;
  flex-shrink: 0;
}

.app-notification-panel__icon-bg {
  width: 44px;
  height: 44px;
  background: var(--primary-light, #e3f2fd);
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #1976d2);
  transition: all var(--transition-duration) ease;
}

.app-notification-panel__item:hover .app-notification-panel__icon-bg {
  transform: scale(1.05);
}

.app-notification-panel__icon-bg--success {
  background: var(--success-light, #e8f5e9);
  color: var(--success, #4caf50);
}

.app-notification-panel__icon-bg--error {
  background: var(--error-light, #ffebee);
  color: var(--error, #f44336);
}

.app-notification-panel__icon-bg--warning {
  background: var(--warning-light, #fff3e0);
  color: var(--warning, #ff9800);
}

.app-notification-panel__icon-bg--info {
  background: var(--info-light, #e3f2fd);
  color: var(--info, #2196f3);
}

.app-notification-panel__unread-indicator {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: var(--error);
  border: 2px solid var(--bg-primary);
  border-radius: 50%;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.app-notification-panel__content-wrapper {
  flex: 1;
  min-width: 0;
}

.app-notification-panel__header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-xs);
  gap: var(--space-sm);
}

.app-notification-panel__item-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
  line-height: var(--line-height-tight);
  transition: color var(--transition-duration) ease;
  flex: 1;
  text-align: left;
}

.app-notification-panel__item-time {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  flex-shrink: 0;
  white-space: nowrap;
  font-weight: var(--font-weight-medium);
}

.app-notification-panel__item-message {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: var(--line-height-relaxed);
  margin: 0;
  text-align: left;
}

.app-notification-panel__actions {
  display: flex;
  gap: var(--space-xs);
  padding: 0 var(--space-md) var(--space-md);
  justify-content: flex-end;
  border-top: 1px solid transparent;
  transition: border-color var(--transition-duration) ease;
}

.app-notification-panel__item:hover .app-notification-panel__actions {
  border-top-color: var(--border-color);
}

.app-notification-panel__action-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-duration) ease;
  font-size: 14px;
}

.app-notification-panel__action-btn:hover {
  transform: scale(1.1);
}

.app-notification-panel__action-btn--check {
  color: var(--success);
  border-color: var(--success-light);
}

.app-notification-panel__action-btn--check:hover {
  background: var(--success);
  color: white;
  border-color: var(--success);
}

.app-notification-panel__action-btn--delete {
  color: var(--error);
  border-color: var(--error-light);
}

.app-notification-panel__action-btn--delete:hover {
  background: var(--error);
  color: white;
  border-color: var(--error);
}

/* Font Awesome icon in notification panel */
.app-notification-panel__icon-svg {
  font-size: 20px;
}

/* Notification variant colors (already defined above, removing duplicates) */
.app-notification-panel__icon-bg--primary {
  background: var(--primary-light, #e3f2fd);
  color: var(--primary, #1976d2);
}

/* Comprehensive background fixes to ensure no transparency */
.app-notification-panel,
.app-notification-panel *,
.app-notification-panel::before,
.app-notification-panel::after {
  /* Disable backdrop filters that might cause transparency */
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.app-notification-panel {
  /* Force solid background with fallbacks */
  background: var(--bg-primary, #ffffff) !important;
  background-color: #ffffff !important; /* Fallback */
}

.app-notification-panel__header {
  /* Force solid header background */
  background: var(--bg-secondary, #f8f9fa) !important;
  background-color: #f8f9fa !important; /* Fallback */
}

.app-notification-panel__item {
  /* Force solid item background */
  background: var(--bg-primary, #ffffff) !important;
  background-color: #ffffff !important; /* Fallback */
}

.app-notification-panel__item--unread {
  /* Force solid unread item background */
  background: var(--primary-lighter, #f0f4ff) !important;
  background-color: #f0f4ff !important; /* Fallback */
}

.app-notification-panel__icon-bg {
  /* Force solid icon background */
  background: var(--primary-light, #e3f2fd) !important;
  background-color: #e3f2fd !important; /* Fallback */
}

/* Dark mode solid backgrounds */
@media (prefers-color-scheme: dark) {
  .app-notification-panel {
    background: #1a1a1a !important;
    background-color: #1a1a1a !important;
  }
  
  .app-notification-panel__header {
    background: #2a2a2a !important;
    background-color: #2a2a2a !important;
  }
  
  .app-notification-panel__item {
    background: #1a1a1a !important;
    background-color: #1a1a1a !important;
  }
  
  .app-notification-panel__item--unread {
    background: #1e3a8a !important;
    background-color: #1e3a8a !important;
  }
  
  .app-notification-panel__icon-bg {
    background: #3b82f6 !important;
    background-color: #3b82f6 !important;
  }
}

/* Responsive design */
@media (max-width: 640px) {
  .app-notification-panel {
    min-height: 280px;
    max-height: 420px;
    border-radius: var(--border-radius-lg);
    margin: 0;
  }
  
  .app-notification-panel__header-content {
    padding: var(--space-sm) var(--space-md);
  }
  
  .app-notification-panel__title {
    font-size: var(--font-size-base);
  }
  
  .app-notification-panel__list {
    max-height: 300px;
    padding: var(--space-xs);
  }
  
  .app-notification-panel__actions {
    flex-direction: column;
    gap: var(--space-xs);
  }
  
  .app-notification-panel__actions .app-button {
    width: 100%;
    padding: var(--space-sm) var(--space-md);
    font-size: var(--font-size-sm);
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .app-notification-panel {
    min-height: 260px;
    max-height: 380px;
  }
  
  .app-notification-panel__list {
    max-height: 240px;
  }
  
  .app-notification-panel__item-content {
    padding: var(--space-sm);
    gap: var(--space-sm);
  }
  
  .app-notification-panel__icon-bg {
    width: 36px;
    height: 36px;
  }
}

/* Reduced motion accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}
</style>
